import React, { useCallback, memo, useState } from 'react';
import {
  RuleBuilderInput,
  RuleBuilderInputValue,
  RuleValueInputChangeEvent,
} from '..';
import * as Styled from './styled';
import { ReportFieldType, ReportField } from '../../../../models/reportField';
import { ReportIncludedFieldFilterOperator } from '../../../../models';
import { SelectOption } from '@leasequery/lq.react-component-library';
import { useAuth } from '../../../../context';
import { getDateRangeFormatConfig, isValidDateByFormat, parseLocaleSpecificDateStringToDefaultFormat } from '../../../../util/dateHelpers';
import { DateRangeMode } from '../../../find-report/report-setting/date-range/DateRange';

interface RuleValueInputProps<T = unknown> {
  reportField: ReportField;
  inputType: ReportFieldType;
  comparisonRule: RuleBuilderInput['comparisonRule'];
  value1: RuleBuilderInputValue<T>;
  value2?: RuleBuilderInputValue<T>;
  value1Err?: string;
  value2Err?: string;
  onValue1Change: RuleValueInputChangeEvent;
  onValue2Change?: RuleValueInputChangeEvent;
}

function RuleValueInput({
  reportField,
  inputType,
  comparisonRule,
  value1,
  value1Err,
  onValue1Change,
  value2,
  value2Err,
  onValue2Change,
}: RuleValueInputProps): JSX.Element {

  const [isFirstDateValid, setIsFirstDateValid] = useState(true);
  const [isSecondDateValid, setIsSecondDateValid] = useState(true);

  const [firstDateInput, setFirstDateInput] = useState(value1 as string);
  const [secondDateInput, setSecondDateInput] = useState(value2 as string);

  const { user } = useAuth();

  const {
    dateFormat,
    placeHolder, 
    inputMask,
    formattedStartDate: formattedFirstDate,
    formattedEndDate: formattedSecondDate
  } = getDateRangeFormatConfig(
    DateRangeMode.FullDate,
    user.localePreference,
    firstDateInput,
    secondDateInput,
    isFirstDateValid,
    isSecondDateValid,
  );

  const createInputChangeHandler = useCallback(
    (
      setDateInput: React.Dispatch<React.SetStateAction<string>>,
      setIsDateValid: React.Dispatch<React.SetStateAction<boolean>>,
      onValueChange: RuleValueInputChangeEvent
    ) => {
      return (e: React.ChangeEvent<HTMLInputElement>) => {
        let defaultFormatValue = e.target.value;

        if (inputType === ReportFieldType.Date) {
            const isDateValid = isValidDateByFormat(defaultFormatValue, dateFormat);
            setIsDateValid(isDateValid);

            if (!isDateValid) {
              setDateInput(defaultFormatValue);
              onValueChange("Invalid date format");
              return;
            }

            // Default date format for the rule input validation and saving
            defaultFormatValue = parseLocaleSpecificDateStringToDefaultFormat(
              defaultFormatValue,
              user.localePreference
            );
          setDateInput(defaultFormatValue);
        }

        onValueChange(defaultFormatValue);
      };
    },
    [dateFormat, inputType, user.localePreference]
  );

  const input1ChangeHandler = createInputChangeHandler(
    setFirstDateInput,
    setIsFirstDateValid,
    onValue1Change
  );

  const input2ChangeHandler = createInputChangeHandler(
    setSecondDateInput,
    setIsSecondDateValid,
    onValue2Change
  );
  const fieldDropdownChangeHandler = useCallback(
    (options: SelectOption<number>[]) => onValue1Change(options),
    [onValue1Change],
  );

  const hasSecondInput =
    comparisonRule === ReportIncludedFieldFilterOperator.between;
  let components: JSX.Element[] = null;
  switch (inputType) {
    case ReportFieldType.Number:
      components = [
        <Styled.ValueInput
          key="value1"
          name="number"
          type="number"
          onChange={input1ChangeHandler}
          value={value1 as string}
          hasError={!!value1Err}
        />,
        hasSecondInput ? (
          <Styled.LinkedElement key="and">and</Styled.LinkedElement>
        ) : null,
        hasSecondInput ? (
          <Styled.ValueInput
            key="value2"
            name="number"
            type="number"
            onChange={input2ChangeHandler}
            value={value2 as string}
            hasError={!!value2Err}
          />
        ) : null,
      ];
      break;

    case ReportFieldType.Date:
      components = [
        <Styled.ValueInputWithMask
          key="value1"
          name="number"
          onChange={input1ChangeHandler}
          value={formattedFirstDate}
          hasError={!!value1Err}
          mask={inputMask}
          placeholder={placeHolder}
        />,
        hasSecondInput ? (
          <Styled.LinkedElement key="and">and</Styled.LinkedElement>
        ) : null,
        hasSecondInput ? (
          <Styled.ValueInputWithMask
            key="value2"
            name="number"
            onChange={input2ChangeHandler}
            value={formattedSecondDate}
            hasError={!!value2Err}
            mask={inputMask}
            placeholder={placeHolder}
          />
        ) : null,
      ];
      break;

    case ReportFieldType.Text:
      components = [
        <Styled.ValueInput
          key="value1"
          name="text"
          type="text"
          onChange={input1ChangeHandler}
          value={value1 as string}
          hasError={!!value1Err}
        />,
        hasSecondInput ? (
          <Styled.LinkedElement key="and">and</Styled.LinkedElement>
        ) : null,
        hasSecondInput ? (
          <Styled.ValueInput
            key="value2"
            name="text"
            type="text"
            onChange={input2ChangeHandler}
            value={value2 as string}
            hasError={!!value2Err}
          />
        ) : null,
      ];
      break;

    case ReportFieldType.DropDown:
      components = [
        <Styled.DropdownInput
          key="value1"
          name="text"
          field={reportField}
          value={value1 as SelectOption<number>[]}
          onChange={fieldDropdownChangeHandler}
          hasError={!!value1Err}
        />,
      ];
      break;

    default:
      components = [
        <div key="InputTypeNotSupport">{`The field type "${inputType}" is not suported yet.`}</div>,
      ];

      break;
  }

  return <Styled.RuleValueInput>{components}</Styled.RuleValueInput>;
}

export default memo(RuleValueInput);
